import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { Banner } from '../components/States';
import { UpgradeOffer } from '../components/UpgradeOffer';
import { SkinFinder } from '../components/SkinFinder';
import { UpgradeFilters, FILTROS_VAZIOS, type UpgradeFiltros } from '../components/UpgradeFilters';
import { useAuth } from '../hooks/useAuth';
import { useCurrency } from '../hooks/useCurrency';
import type { TemplateModel, UpgradeQuote, ListingModel } from '../api/types';
import { itemPorId, reais, trackAddShippingInfo, trackBeginCheckout, trackUpgradeQuote } from '../lib/tracking';
import { abrirAbaEmBranco, enviarAoAtendimento } from '../lib/whatsapp';

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

interface Desejada {
  templateId: number;
  /** Anúncio escolhido. Sem ele, a loja decide qual peça comprar. */
  listingId?: string;
  /** Float da peça escolhida — é o que distingue duas do mesmo desgaste. */
  float?: number | null;
  /** Inspeção no jogo da peça escolhida, quando o anúncio traz o link. */
  inspectUrl?: string | null;
  exterior?: string | null;
  /** Preço em yuan, como o catálogo mostra — o total do cabeçalho sai daqui. */
  precoCny?: number | null;
  name: string;
  iconUrl: string | null;
  qty: number;
}

/**
 * Chave de uma linha da lista.
 *
 * Não pode ser só o templateId: o cliente pode querer duas peças do MESMO
 * desgaste com floats diferentes, e elas são itens distintos do pedido.
 */
const chaveDe = (d: { templateId: number; listingId?: string }) => d.listingId ?? `t${d.templateId}`;

/**
 * Upgrade: as skins do cliente viram crédito e pagam (parte de) outra skin.
 *
 * A tela é uma mesa de troca de três colunas: o que sai à esquerda, o que
 * entra à direita e, no meio, a diferença com os filtros que governam o
 * catálogo. As duas pontas usam preços diferentes de propósito — o que entra é
 * avaliado pelo preço de compra (com o desconto), e o que sai custa o preço de
 * venda do site. Ver as duas colunas ao mesmo tempo é o que impede a diferença
 * de virar surpresa no fim.
 */
export function UpgradePage() {
  const navigate = useNavigate();
  const { loginUrl, user } = useAuth();
  const { money } = useCurrency();
  const [fechando, setFechando] = useState(false);
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [desejadas, setDesejadas] = useState<Desejada[]>([]);
  const [cotacao, setCotacao] = useState<UpgradeQuote | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [filtros, setFiltros] = useState<UpgradeFiltros>(FILTROS_VAZIOS);
  const [fechada, setFechada] = useState<'esq' | 'dir' | null>(null);

  // O crédito da coluna da esquerda alimenta o filtro da direita.
  const [credito, setCredito] = useState(0);
  const tela = useRef<HTMLDivElement>(null);
  const [altura, setAltura] = useState<string>('70vh');

  /**
   * A mesa ocupa o que sobrou da janela.
   *
   * A altura é medida em vez de calculada com um número mágico porque o topo
   * do site muda de tamanho: a barra some no celular e cresce quando entra um
   * aviso de conta. Cada coluna rola por dentro, e a página não rola.
   */
  useEffect(() => {
    const medir = () => {
      const topo = tela.current?.getBoundingClientRect().top ?? 0;
      setAltura(`${Math.max(420, window.innerHeight - topo - 24)}px`);
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, []);

  function adicionar(variant: TemplateModel, anuncio?: ListingModel) {
    setCotacao(null);
    const nova: Desejada = {
      templateId: variant.id,
      listingId: anuncio?.commodityNo,
      float: anuncio?.float ?? null,
      inspectUrl: anuncio?.inspectUrl ?? null,
      exterior: variant.exterior?.pt ?? null,
      precoCny: anuncio?.price ?? variant.price,
      name: variant.name,
      // A foto real da peça, quando o anúncio traz uma: num upgrade o cliente
      // está escolhendo aquela peça, não o desenho da skin.
      iconUrl: anuncio?.imageUrl ?? anuncio?.iconUrl ?? variant.iconUrl,
      qty: 1,
    };

    setDesejadas((prev) => {
      const existente = prev.find((d) => chaveDe(d) === chaveDe(nova));
      // Anúncio é uma peça só: clicar de novo no mesmo não soma quantidade.
      if (existente) {
        if (nova.listingId) return prev;
        return prev.map((d) => (chaveDe(d) === chaveDe(nova) ? { ...d, qty: d.qty + 1 } : d));
      }
      return [...prev, nova];
    });
  }

  function remover(chave: string) {
    setCotacao(null);
    setDesejadas((prev) => prev.filter((d) => chaveDe(d) !== chave));
  }

  async function calcular() {
    setOcupado(true);
    setErro(null);
    try {
      const quote = await api.upgradeQuote(
        selecionados,
        desejadas.map((d) => ({ templateId: d.templateId, listingId: d.listingId, qty: d.qty })),
      );
      trackUpgradeQuote({ aPagarBrl: quote.aPagarBrl, creditoBrl: quote.credito.totalBrl });
      setCotacao(quote);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui calcular a troca.');
    } finally {
      setOcupado(false);
    }
  }

  function fecharPedido() {
    trackBeginCheckout(
      'upgrade',
      desejadas.map((d) => ({
        ...itemPorId(d.templateId, d.name),
        ...(d.exterior ? { item_variant: d.exterior } : {}),
        ...(reais(d.precoCny) !== undefined ? { price: reais(d.precoCny) } : {}),
        quantity: d.qty,
      })),
      cotacao?.aPagarBrl,
    );
    // Aberta ainda dentro do clique: depois de esperar a API o navegador bloqueia.
    const aba = abrirAbaEmBranco();
    setFechando(true);
    setErro(null);
    void api
      .criarPedido({
        kind: 'upgrade',
        assetIds: selecionados,
        items: desejadas.map((d) => ({ templateId: d.templateId, listingId: d.listingId, qty: d.qty })),
      })
      .then(async ({ order }) => {
        trackAddShippingInfo(order);
        // Já vai ao atendimento, como a sacola: sem o segundo clique na tela do
        // pedido, que deixava upgrades parados como "Não enviado".
        await enviarAoAtendimento(order, aba);
        navigate(`/pedido/${order.code}`);
      })
      .catch((e) => {
        aba?.close();
        setErro(e instanceof ApiError ? e.message : 'Não consegui abrir o pedido.');
      })
      .finally(() => setFechando(false));
  }

  const alterarFiltros = useCallback((patch: Partial<UpgradeFiltros>) => setFiltros((p) => ({ ...p, ...patch })), []);
  const alterarSelecao = useCallback((ids: string[]) => {
    setSelecionados(ids);
    setCotacao(null);
  }, []);

  /**
   * O botão da coluna do meio conta a história inteira da troca: entrar,
   * calcular e fechar são o mesmo passo em momentos diferentes.
   */
  const acao = !user
    ? { rotulo: 'Entrar para trocar', href: loginUrl }
    : cotacao
      ? {
          rotulo: fechando ? 'Enviando ao atendimento…' : 'Fechar upgrade e enviar no WhatsApp',
          onClick: fecharPedido,
          desabilitado: fechando,
        }
      : {
          rotulo: ocupado ? 'Calculando…' : 'Calcular a troca',
          onClick: () => void calcular(),
          desabilitado: ocupado || selecionados.length === 0 || desejadas.length === 0,
        };

  /**
   * A diferença só aparece depois da cotação.
   *
   * Estimar na tela exigiria converter o catálogo (em yuan) e reaplicar as
   * regras de preço no cliente: um número que não é o que será cobrado. O
   * servidor é quem fecha a conta.
   */
  const diferenca = cotacao
    ? cotacao.aPagarBrl > 0
      ? { rotulo: 'Você completa', valor: brl(cotacao.aPagarBrl), positivo: false }
      : { rotulo: 'Sobra de crédito', valor: brl(cotacao.sobraBrl), positivo: true }
    : null;

  /**
   * O total da direita.
   *
   * Antes da cotação ele soma os preços do jeito que os cards mostram — em
   * yuan convertido —, para o cabeçalho não ficar mudo enquanto o cliente
   * escolhe. Depois da cotação vale o número do servidor, que é o que será
   * cobrado.
   */
  const totalDesejado = cotacao
    ? brl(cotacao.totalDesejadoBrl)
    : money(desejadas.reduce((s, d) => s + (d.precoCny ?? 0) * d.qty, 0));

  return (
    <div
      ref={tela}
      className={`upgrade-tela ${fechada === 'esq' ? 'esq-fechada' : ''} ${fechada === 'dir' ? 'dir-fechada' : ''}`}
      style={{ height: altura }}
    >
      <UpgradeOffer
        selecionados={selecionados}
        onChange={alterarSelecao}
        onTotal={setCredito}
        recolhido={fechada === 'esq'}
        onRecolher={() => setFechada((f) => (f === 'esq' ? null : 'esq'))}
      />

      <UpgradeFilters
        filtros={filtros}
        onChange={alterarFiltros}
        acao={acao}
        diferenca={diferenca}
        credito={credito > 0 ? brl(credito) : null}
        resultado={
          <>
            {erro && <Banner kind="error">{erro}</Banner>}

            {cotacao && (
              <div className="up-resultado">
                {/* Nada do que o servidor recusou pode sumir: é a diferença
                    entre o que o cliente marcou e o que a troca aceita. */}
                {cotacao.credito.recusados.length > 0 && (
                  <Banner kind="warn">
                    {cotacao.credito.recusados.length} item(ns) do inventário ficaram de fora:{' '}
                    {cotacao.credito.recusados.map((r) => r.motivo).join(' · ')}
                  </Banner>
                )}
                {cotacao.indisponiveis.length > 0 && (
                  <Banner kind="warn">
                    {cotacao.indisponiveis.length} skin(s) desejada(s) sem disponibilidade agora.
                  </Banner>
                )}

                <div className="up-resumo">
                  <span>
                    Crédito <b>{brl(cotacao.credito.totalBrl)}</b>
                  </span>
                  <span>
                    Escolhidas <b>{brl(cotacao.totalDesejadoBrl)}</b>
                  </span>
                </div>

                <button className="btn ghost small" onClick={() => setCotacao(null)}>
                  Refazer
                </button>

                <p className="up-nota">
                  O que entra é avaliado {cotacao.credito.discountPct}% abaixo do mercado de origem; o que sai custa
                  o preço do catálogo. O fechamento é feito pelo atendimento, pelo trade link do seu cadastro.
                </p>
              </div>
            )}
          </>
        }
      />

      <SkinFinder
        credito={credito}
        filtros={filtros}
        onFiltros={alterarFiltros}
        onEscolher={(variant, _grupo, anuncio) => adicionar(variant, anuncio)}
        total={totalDesejado}
        recolhido={fechada === 'dir'}
        onRecolher={() => setFechada((f) => (f === 'dir' ? null : 'dir'))}
      >
        {desejadas.length > 0 && (
          <ul className="up-escolhidas">
            {desejadas.map((d) => (
              <li key={chaveDe(d)}>
                {/* A miniatura abre a inspeção no jogo: no upgrade o cliente
                    está trocando peças concretas, e querer olhar a peça antes
                    de confirmar é o passo natural. */}
                {d.iconUrl ? (
                  d.inspectUrl ? (
                    <a href={d.inspectUrl} title="Inspecionar esta peça no CS2">
                      <img src={d.iconUrl} alt="" />
                    </a>
                  ) : (
                    <img src={d.iconUrl} alt="" />
                  )
                ) : (
                  <span className="sem-img" />
                )}
                <span className="nome">
                  {d.name}
                  {/* Float e desgaste identificam a peça escolhida; sem anúncio
                      a linha continua sendo do template. */}
                  {d.listingId && (
                    <span className="dim bloco">
                      {d.exterior ?? ''}
                      {d.float !== null && d.float !== undefined ? ` · float ${d.float.toFixed(8)}` : ''}
                      {d.inspectUrl && (
                        <>
                          {' · '}
                          <a href={d.inspectUrl} title="Inspecionar esta peça no CS2">
                            🔍 inspecionar
                          </a>
                        </>
                      )}
                    </span>
                  )}
                </span>
                {!d.listingId && <span className="dim">×{d.qty}</span>}
                <button className="btn ghost small" onClick={() => remover(chaveDe(d))}>
                  Remover
                </button>
              </li>
            ))}
          </ul>
        )}
      </SkinFinder>
    </div>
  );
}
